import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTheme } from '@mui/material/styles';
import { allergiesAnswers } from '../../../constants';

import { Button, RadioGroup, Box, Alert, IRadioProps } from '@/shared/ui';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { OnboardingController } from '@/shared/endpoints/onboarding';
import { CustomersStepTypeEnum, ICustomersUserDetails } from '@/shared/endpoints/customers';
import { AllergyDTO } from '@/shared/types/interfaces/transfer-prescriptions';
import { ConditionDTO, IConvertedAllergy, IConvertedCondition } from '@/shared/endpoints/onboarding/models/dtos';

import WavingImage from '@/assets/images/waving.png';
import HIPPA from '@/assets/images/hippa.png';
import HIPPAICON from '@/assets/images/hippa-icon.png';

import { constructUnknownCondition, getConditionIdentifier } from '../helpers';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';

import HelpArrowRotatedIcon from '@/assets/icons/help-arrow-rotated.svg';
import { AutoCompleteSearch } from '@/shared/ui/autocomplete/autoComplete-search';

interface Allergy {
    allergyType: string;
    allergenDescription: string;
    allergenFdbId: number;
}

interface Condition {
    code: string;
    qualifierType: string;
    shortDescription: string;
}

interface AboutYouAllergiesDetails {
    allergies?: AllergyDTO[];
    conditions?: ConditionDTO[];
}

interface AboutYouAllergiesDefaultValues extends AboutYouAllergiesDetails {
    details?: ICustomersUserDetails;
}

interface AboutYouAllergiesAndConditionsFormProps {
    onFormSubmit: (values: AllergiesFormValues) => void;
    onGoBack: () => void;
    defaultValues?: AboutYouAllergiesDefaultValues;
    details?: AboutYouAllergiesDetails;
    loading?: boolean;
    showHelper?: boolean;
}
interface AboutYouAllergiesropsAndConditions {
    formArttributes: AboutYouAllergiesAndConditionsFormProps;
    introductionPassed: boolean;
}
export const HIPAAComponent = () => (
    <div data-testid="about-you-hippa-container" className="flex w-full flex-row items-center justify-center">
        <img src={HIPPAICON} alt="HIPPA" />
        <img src={HIPPA} alt="HIPPA" />
    </div>
);
export const MainTitle = () => (
    <h1 data-testid="about-you-allergies-and-condition-main-title" className="about-you-title allergies-and-conditions-main-title desktop-min:about-you-title-lg desktop-min:allergies-and-conditions-main-title-lg w-[auto] text-center desktop-min:w-full desktop-min:text-left">
        Please inform us of any <span className="text-mint-green">Medical Conditions or Allergies</span> you may have.
    </h1>
);

export const Illustration = () => (
    <div data-testid="about-you-illustration-container" className="static flex w-full flex-col items-center justify-center md:relative desktop-min:block">
        <div className="illusTextContainer lg-tablet:min-w-[22rem] relative max-w-[29.1rem] tablet:min-w-[20rem] desktop-min:max-w-[36rem]">
            <div className="illus-text">
                <span data-testid="about-you-illustration-privacy-text" className="sm-max:illusTextSmall">We take your privacy seriously and strictly abide by</span>
                <span data-testid="about-you-illustration-hippa-privacy-text" className="sm-max:illusTextSmall font-atkinsonBold italic"> the HIPAA Privacy Rule </span>
                <span data-testid="about-you-illustration-confidentiality-text" className="sm-max:illusTextSmall">to ensure the confidentiality of your information.</span>
            </div>
            <img
                src={WavingImage}
                alt="About you image"
                className="waiving lg-tablet:right-[1rem] -bottom-[10rem] hidden h-auto w-[8.8125rem] md:right-[2rem] xl-min:block desktop-min:absolute"
            />
        </div>
    </div>
);

export interface AllergiesFormValues {
    allergies: IConvertedAllergy[];
    conditions: IConvertedCondition[];
    hasAllergies: number | null;
    hasConditions: number | null;
    currentStep?: CustomersStepTypeEnum;
}

export const AllergiesAndConditionsForm: React.FC<AboutYouAllergiesAndConditionsFormProps> = props => {
    const theme = useTheme();
    const [submitted, setSubmitted] = useState(false);
    const initialValues = useMemo(
        (): AllergiesFormValues => ({
            conditions: props.defaultValues?.conditions
                ? props.defaultValues.conditions.map((_item: Condition) => ({
                    ..._item,
                    label: _item.shortDescription,
                    value: getConditionIdentifier(_item)
                }))
                : [],
            allergies: props.defaultValues?.allergies
                ? props.defaultValues.allergies.map((_item: Allergy) => ({
                    ..._item,
                    label: _item.allergenDescription,
                    value: _item.allergenFdbId
                }))
                : [],
            hasAllergies: props.defaultValues?.details?.allergies === undefined ? null : Number(props.defaultValues.details.allergies),
            hasConditions: props.defaultValues?.details?.conditions === undefined ? null : Number(props.defaultValues.details.conditions)
        }),
        [props.defaultValues]
    );

    const formik = useFormik({
        validateOnChange: true,
        initialValues,
        onSubmit: values => {
            props.onFormSubmit(values);
        }
    });

    const [allergiesOpts, setAllergieOpts] = React.useState<IConvertedAllergy[]>([]);
    const [conditionOpts, setConditionOpts] = React.useState<IConvertedCondition[]>([]);
    const [isFormLoading, setIsFormLoading] = React.useState<boolean>(false);
    const [isAllergiesModalOpen, setIsAllergiesModalOpen] = React.useState<boolean>(false);
    const [isConditionsModalOpen, setIsConditionsModalOpen] = React.useState<boolean>(false);
    const [nextHasCondition, setNextHasCondition] = React.useState<IRadioProps | null>(null);
    const [nextHasAllergy, setNextHasAllergy] = React.useState<IRadioProps | null>(null);

    const handleConditionsChange = useCallback(
        (condition: IConvertedCondition) => {
            const item = conditionOpts.find(_item => _item.value === condition.value);

            const existing = formik.values.conditions || [];

            if (item && existing.find(_item => getConditionIdentifier(_item) === getConditionIdentifier(item))) {
                return;
            }

            formik.setFieldValue('conditions', [condition, ...existing]);
        },
        [formik, conditionOpts]
    );

    const handleAllergiesChange = (allergy: IConvertedAllergy) => {
        const { value } = allergy;
        const item = allergiesOpts.find(_item => _item.allergenFdbId === value);
        const existing = formik.values.allergies || [];
        if (item && existing.find(_item => _item.allergenFdbId === item.allergenFdbId)) {
            return;
        }

        formik.setFieldValue('allergies', [allergy, ...existing]);
    };

    // like allergies, when the user tries to update the conditions, we need to update the options
    const handleConditionsInputChange = useCallback(async (value: string, signal: AbortSignal): Promise<ConditionDTO[]> => {
        setIsFormLoading(true);

        try {
            const res =
                (await OnboardingController.searchConditions({
                    term: value,
                    limit: 5
                }, signal)) || [];

            const resWithUnknownCondition = [constructUnknownCondition(value), ...res];

            const allConditions = resWithUnknownCondition.map(_item => ({
                label: _item.shortDescription,
                value: getConditionIdentifier(_item),
                ..._item
            }));

            setConditionOpts(resWithUnknownCondition as IConvertedCondition[]);
            return allConditions;
        } catch (error) {
            setConditionOpts([]);
            return [];
        } finally {
            setIsFormLoading(false);
        }
    }, []);

    // when input of the auto-complete changes, we need to update the options for allergies
    const handleAllergiesInputChange = useCallback(async (value: string, signal: AbortSignal): Promise<AllergyDTO[]> => {
        setIsFormLoading(true);
        try {
            const res: Array<AllergyDTO> = await OnboardingController.searchAllergies({
                term: value,
                limit: 5
            }, signal);

            const allAllergies = res?.length
                ? res.map((_item: AllergyDTO) => ({
                    label: _item.allergenDescription,
                    value: _item.allergenFdbId,
                    ..._item
                }))
                : [];

            setAllergieOpts(allAllergies);
            return allAllergies;
        } catch (error) {
            setAllergieOpts([]);
            return [];
        } finally {
            setIsFormLoading(false);
        }
    }, []);

    // when useer switch between "yes" and "no", do correspodning check, and pop up the modal if necessary
    const handleHasAllergiesClick = useCallback(
        (item: IRadioProps) => {
            // from yes to no, need the confirm
            if (formik.values.hasAllergies === item.value) {
                return;
            }
            if (item.value !== 1 && formik.values.allergies && formik.values.allergies.length) {
                //  show modal and wait for confirm
                setNextHasAllergy(item);
                return setIsAllergiesModalOpen(true);
            }
            formik.setFieldValue('hasAllergies', item.value);
        },
        [formik]
    );

    // the options of conditions are 1,2,3
    const handleHasConditionsClick = useCallback(
        (item: IRadioProps) => {
            if (formik.values.hasConditions === item.value) {
                return;
            }
            if (formik.values.hasConditions === 1 && item.value !== 1 && formik.values.conditions && formik.values.conditions.length) {
                // show modal and wait for confirm
                setNextHasCondition(item);
                return setIsConditionsModalOpen(true);
            }
            formik.setFieldValue('hasConditions', item.value);
            // setHasConditions(item.value);
        },
        [formik]
    );

    const handleAllergiesCofirm = useCallback(() => {
        setIsAllergiesModalOpen(false);
        formik.setFieldValue('allergies', []);
        formik.setFieldValue('hasAllergies', nextHasAllergy?.value);
        formik.setFieldTouched('allergies', false);
    }, [formik, nextHasAllergy]);

    const handleConditionsConfirm = useCallback(() => {
        setIsConditionsModalOpen(false);
        setNextHasCondition(null);
        formik.setFieldValue('hasConditions', nextHasCondition?.value);
        formik.setFieldValue('conditions', []);
        formik.setFieldTouched('conditions', false);
    }, [formik, nextHasCondition?.value]);

    const handleAllergiesRemove = useCallback(
        (item: IConvertedAllergy) => {
            const remian = formik.values.allergies?.filter(_item => _item.value !== item.value);
            formik.setFieldValue('allergies', remian);
            formik.setFieldTouched('allergies', true);
        },
        [formik]
    );

    const handleConditionsRemove = useCallback(
        (item: IConvertedCondition) => {
            const remian = formik.values.conditions?.filter(_item => _item.value !== item.value);
            formik.setFieldValue('conditions', remian);
            formik.setFieldTouched('conditions', true);
        },
        [formik]
    );

    const validateForm = useCallback(() => {
        const { hasAllergies, hasConditions, allergies, conditions } = formik.values;
        formik.setFieldError('hasAllergies', undefined);
        formik.setFieldError('hasConditions', undefined);
        formik.setFieldError('allergies', undefined);
        formik.setFieldError('conditions', undefined);
        let hasError = false;
        if (hasAllergies === null || hasAllergies === undefined) {
            formik.setFieldError('hasAllergies', 'Please add your medication allergies to continue');
            hasError = true;
        } else if (hasAllergies === 1 && (!allergies || !allergies.length)) {
            formik.setFieldError('allergies', 'Please add your medication allergies to continue');
            hasError = true;
        }
        if (hasConditions === null || hasConditions === undefined) {
            formik.setFieldError('hasConditions', 'Please add your medical conditions to continue');
            hasError = true;
        } else if (hasConditions === 1 && (!conditions || !conditions.length)) {
            formik.setFieldError('conditions', 'Please add your medical conditions to continue');
            hasError = true;
        }

        return hasError;
    }, [formik]);

    const formikValues = formik.values;

    useEffect(() => {
        if (submitted) setTimeout(() => validateForm(), 0);
    }, [submitted, formikValues]);

    return (
        <div data-testid="allergies-wrapper">
            <Box
                component="form"
                data-testid="allergies-form"
                onSubmit={(e: FormEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setSubmitted(true);
                    const hasError = validateForm();
                    // @ts-ignore-next-line
                    if (!hasError) formik.handleSubmit(e);
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    [theme.breakpoints.down('lg')]: {
                        gap: '20px'
                    },
                    width: '100%',
                    backgroundColor: '#fff'
                }}
                paddingLg="0"
                isLoading={isFormLoading}
            >
                <div className="flex w-full flex-col gap-[1.56rem] rounded-lg bg-white md-min:gap-[1.88rem]">
                    <section className="allergies-section aboutyou-form-sections">
                        <h3 className="onboarding-section-title md:onboarding-section-title-lg w-full text-left">
                            Do you have any allergies to medications?
                        </h3>
                        <div className="aboutyou-form-section-content desktop-min:aboutYouFormSectionContentLg flex w-full flex-col gap-[1.56rem] rounded-[0.8125rem]">
                            <div className="about-you-checkbox flex-no-wrap flex flex-col">
                                <RadioGroup
                                    controlled
                                    items={allergiesAnswers}
                                    name="allergies"
                                    dataTestId={'allergies'}
                                    radioGroupClassName="allergies-and-conditions-radio-group flex-row"
                                    buttonClassName="allergies-and-conditions-radio-button"
                                    value={formik.values.hasAllergies}
                                    onOptionClick={handleHasAllergiesClick}
                                />
                                {formik.values.hasAllergies === 1 && (
                                    <div className="w-full">
                                        <p className="mb-[0.44rem] mt-[1.03rem] text-left font-atkinsonBold">Search the medication allergy</p>
                                        <AutoCompleteSearch
                                            scrollToTitle
                                            uniqueIdentifier="allergiesFinder"
                                            placeholder="Type allergy here"
                                            onChange={handleAllergiesChange}
                                            onSearch={handleAllergiesInputChange}
                                            popoverPlaceholder="Type one allergy at a time"
                                            labelKey="label"
                                            error={
                                                !!formik.errors.hasAllergies ||
                                                !!(formik.values.hasAllergies === 1 && formik.errors.allergies)
                                            }
                                            popoverHelper={
                                                <i className="absolute right-[26px] top-[95px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                                                    <p className="mr-2 mt-2">
                                                        It's <b>3x faster</b> <br /> using voice!
                                                    </p>
                                                    <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
                                                </i>
                                            }
                                            onSelectedRemove={handleAllergiesRemove}
                                            selected={formik.values.allergies || []}
                                            inputVariant="advanced"
                                            popoverInputVariant="advanced"
                                            popoverTitle="Search the medication allergy"
                                        />
                                    </div>
                                )}
                                {formik.errors.hasAllergies && (
                                    <div className="mt-[1.25rem] sm-max:mt-[1.38rem]">
                                        <Alert label={formik.errors.hasAllergies as string} />
                                    </div>
                                )}
                                {formik.values.hasAllergies === 1 && formik.errors.allergies && (
                                    <div className="mt-[1.25rem] sm-max:mt-[1.38rem]">
                                        <Alert label={formik.errors.allergies as string} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                    <section className="conditions-section aboutyou-form-sections">
                        <h3 className="onboarding-section-title md:onboarding-section-title-lg w-full text-left">
                            Do you have any medical conditions?
                        </h3>
                        <div className="aboutyou-form-section-content desktop-min:aboutYouFormSectionContentLg flex w-full flex-col rounded-[0.8125rem]">
                            <RadioGroup
                                items={allergiesAnswers}
                                name="conditions"
                                dataTestId={'conditions'}
                                radioGroupClassName="allergies-and-conditions-radio-group conditions-radio-group flex-row"
                                buttonClassName="allergies-and-conditions-radio-button"
                                value={formik.values.hasConditions}
                                onOptionClick={handleHasConditionsClick}
                                controlled
                            />
                            {formik.values.hasConditions === 1 && (
                                <div className="w-full">
                                    <p className="mb-[0.44rem] mt-[1.03rem] text-left font-atkinsonBold">Search the medical condition</p>

                                    <AutoCompleteSearch
                                        scrollToTitle
                                        uniqueIdentifier="conditionsFinder"
                                        placeholder="Type Condition here"
                                        popoverPlaceholder="Type one condition at a time"
                                        labelKey="label"
                                        onChange={handleConditionsChange}
                                        onSearch={handleConditionsInputChange}
                                        onSelectedRemove={handleConditionsRemove}
                                        inputVariant="advanced"
                                        popoverInputVariant="advanced"
                                        popoverTitle="Search the medical condition"
                                        error={
                                            !!formik.errors.hasConditions ||
                                            !!(formik.values.hasConditions === 1 && formik.errors.conditions)
                                        }
                                        popoverHelper={
                                            <i className="absolute right-[26px] top-[95px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                                                <p className="mr-2 mt-2">
                                                    It's <b>3x faster</b> <br /> using voice!
                                                </p>
                                                <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
                                            </i>
                                        }
                                        selected={formik.values.conditions || []}
                                        startAdornmentSx={{
                                            '@media only screen and (min-width: 768px) and (max-width: 950px)': {
                                                display: 'none'
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {formik.errors.hasConditions && (
                                <div className="mt-[0.94rem] sm-max:mt-[1.38rem]">
                                    <Alert label={formik.errors.hasConditions as string} />
                                </div>
                            )}
                            {formik.values.hasConditions === 1 && formik.errors.conditions && (
                                <div className="mt-[0.94rem] sm-max:mt-[1.38rem]">
                                    <Alert label={formik.errors.conditions as string} />
                                </div>
                            )}
                        </div>
                    </section>
                </div>
                <div className="flex w-full flex-col items-center justify-start">
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        showEndIcon
                        className="mb-[1.25rem] w-full md-max:mb-[0.44rem]"
                        loading={props.loading}
                        testId="button-allergies-conditions-continue"
                    >
                        Save & Continue
                    </Button>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => props.onGoBack && props.onGoBack()}
                        className="w-full"
                        disabled={props.loading}
                        testId="button-allergies-conditions-back"
                    >
                        Back
                    </Button>
                </div>
                <section className="conditions-section aboutyou-form-sections mt-[12px]">
                    <HIPAAComponent />
                </section>
            </Box>

            <ConfirmModal
                open={isAllergiesModalOpen}
                onConfirm={handleAllergiesCofirm}
                onCancel={() => setIsAllergiesModalOpen(false)}
                title="Are you sure you want to remove your selected medical allergies?"
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            />

            <ConfirmModal
                open={isConditionsModalOpen}
                onConfirm={handleConditionsConfirm}
                onCancel={() => setIsConditionsModalOpen(false)}
                title="Are you sure you want to remove your selected medical conditions?"
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            />
        </div>
    );
};

export const AllergiesAndConditionsSideComponent = () => {
    return (
        <div data-testid="about-you-allergies-and-conditions-side-container" className="flex flex-col items-center justify-center xl-min:items-start">
            <div className="top-img-container flex w-full flex-row items-center justify-center desktop-min:hidden">
                <img src={WavingImage} alt="About you image" className="waiving block h-auto w-[7rem]" />
            </div>
            <MainTitle />
            <div className="hidden xl-min:mr-[8rem] tablet:mr-[4rem] desktop-min:mt-[3.92rem] desktop-min:block desktop:mb-[3.92rem]">
                <Illustration />
            </div>
        </div>
    );
};

export const AllergiesAndConditionsComponent: React.FC<AboutYouAllergiesropsAndConditions> = props => {
    return (
        <SplitLayout
            sideContent={<AllergiesAndConditionsSideComponent />}
            mainContent={
                <div className="box-border flex w-full flex-1 flex-col items-center justify-start desktop-min:items-start">
                    <div className="box-wrapper desktop-min:box-wrapper-lg xl-min:box-wrapper-xl box-border w-full bg-white">
                        <AllergiesAndConditionsForm {...props.formArttributes} showHelper={!props.introductionPassed} />
                    </div>
                    <div className="mb-[14.7rem] mt-[2.75rem] desktop-min:mb-0 desktop-min:mt-0 desktop-min:hidden">
                        <Illustration />
                    </div>
                </div>
            }
        />
    );
};

export default AllergiesAndConditionsComponent;
